import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Avatar, Button, EmptyState, Icon, Skeleton, Tabs, fmtNum } from "../components/ui";
import CharacterCard from "../components/character/Card";
import PostCard from "../components/social/PostCard";
import ReportModal from "../components/social/ReportModal";
import useMe from "../components/social/useMe";
import { useAsync } from "../hooks";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useI18n } from "../services/i18n";
import content from "../services/api-content";
import "../styles/content.css";

export const safeLink = (u) => (/^https?:\/\//i.test(String(u || "")) ? u : /^[\w.-]+\.[a-z]{2,}/i.test(String(u || "")) ? `https://${u}` : "");

function Posts({ userId, type, meId, isSuper }) {
  const { data, loading, error, reload } = useAsync(() => content.posts.list({ type, userId }), [userId, type]);
  if (error) return <div className="error-text">{error}</div>;
  if (loading) return <Skeleton height={120} />;
  if (!data.items.length) return <EmptyState icon={type === "short" ? "🎬" : "📝"} title={type === "short" ? "ยังไม่มีคลิปสั้น" : "ยังไม่มีโพสต์"} />;
  return <div className="stack">{data.items.map((p) => <PostCard key={p.id} p={p} meId={meId} isSuper={isSuper} onDeleted={reload} />)}</div>;
}

export default function Creator() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useI18n();
  const { profile, isSuperUser } = useAuth();
  const me = useMe();
  const [tab, setTab] = useState("characters");
  const [report, setReport] = useState(false);
  const [follow, setFollow] = useState(null);
  const { data: c, loading, error } = useAsync(() => content.creators.profile(id), [id]);

  if (error) return <EmptyState icon="🙈" title="ไม่พบครีเอเตอร์นี้" action={<Button onClick={() => navigate("/?tab=creators")}>ดูครีเอเตอร์อื่น</Button>}>{error}</EmptyState>;
  if (loading) return <div className="stack"><Skeleton height={150} radius={24} /><Skeleton height={60} /></div>;

  const isMe = !!me && me.id === c.id;
  const isFollowing = follow?.following ?? !!c.isFollowing;
  const followers = follow?.count ?? (Number(c.followerCount) || 0);
  const toggle = async () => {
    try { const r = await content.creators.follow(c.id); setFollow({ following: r.following, count: r.followerCount ?? followers + (r.following ? 1 : -1) }); } catch (e) { toast(e.message, "error"); }
  };
  const links = (c.socialLinks || []).map(safeLink).filter(Boolean);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <div className="cover" style={{ background: c.backgroundImage ? `center / cover url(${c.backgroundImage})` : "var(--grad-soft)" }} />
      <div className="profile-head">
        <Avatar name={c.displayName} colors={c.colors} src={c.photoURL} size={92} />
        <div className="grow" style={{ paddingBottom: 6, minWidth: 180 }}><h1>{c.displayName}</h1><div className="muted">@{c.username || "-"}</div></div>
        <div className="row" style={{ paddingBottom: 6 }}>
          {isMe ? <Link className="btn" to="/me">แก้ไขโปรไฟล์</Link> : <>
            <Button variant={isFollowing ? "ghost" : "primary"} aria-pressed={isFollowing} onClick={toggle}>{isFollowing ? t("กำลังติดตาม") : t("ติดตาม")}</Button>
            <Button icon variant="ghost" aria-label="รายงานผู้ใช้" onClick={() => setReport(true)}><Icon name="shield" /></Button>
          </>}
        </div>
      </div>
      {c.bio ? <p className="mt-16" style={{ whiteSpace: "pre-wrap" }}>{c.bio}</p> : null}
      {links.length ? <div className="chip-row mt-8">{links.map((l) => <a key={l} className="chip chip--sm" href={l} target="_blank" rel="noopener noreferrer nofollow">🔗 {l.replace(/^https?:\/\//, "").slice(0, 30)}</a>)}</div> : null}
      <div className="stats card mt-16" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="stat"><b>{c.characterCount ?? c.characters.length}</b><span>{t("ตัวละคร")}</span></div>
        <div className="stat"><b>{fmtNum(followers)}</b><span>{t("ผู้ติดตาม")}</span></div>
        <div className="stat"><b>{fmtNum(c.messageCount || 0)}</b><span>{t("ข้อความ")}</span></div>
      </div>
      <div className="mt-24">
        <Tabs variant="line" value={tab} onChange={setTab} tabs={[{ value: "characters", label: t("ตัวละคร") }, { value: "posts", label: t("โพสต์") }, { value: "shorts", label: t("คลิปสั้น") }]} />
        <div className="mt-16">
          {tab === "characters" ? (
            c.characters.length ? <div className="grid-cards">{c.characters.map((x) => <CharacterCard key={x.id} c={x} userName={profile?.displayName} />)}</div> : <EmptyState icon="🎭" title="ยังไม่มีตัวละครที่เผยแพร่" />
          ) : <Posts userId={c.id} type={tab === "posts" ? "post" : "short"} meId={me?.id} isSuper={isSuperUser} />}
        </div>
      </div>
      <ReportModal open={report} onClose={() => setReport(false)} targetType="user" targetId={c.id} title="รายงานผู้ใช้" />
    </div>
  );
}
